import { useMemo, useState } from 'react';
import { ProSidebar, Menu } from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { BiArrowFromRight } from 'react-icons/bi';
import { TruktWatermark } from '../../assets';
import { SidebarLogo } from './SidebarLogo';
import { SidebarProps } from './Sidebar.types';
import { SidebarDivider } from './SidebarDivider';
import { Typography } from '../Typography';
import { SidebarMenuItem } from './SidebarMenuItem';
import { getMenuHierarchy } from './Sidebar.helpers';
import { SidebarSubMenu } from './SidebarSubMenu';

export const Sidebar = ({
  items = [],
  accountInfo,
  version,
  onLogoutClick,
  logo,
  itemsSuffix,
}: SidebarProps) => {
  const menuHierarchy = useMemo(() => getMenuHierarchy(items), [items]);
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string>(location.pathname);

  const onOpenChange = (menuId: string) => {
    if (menuId !== openMenu) {
      const menuIdx = menuHierarchy[openMenu]?.findIndex((id) => id === menuId) ?? -1;
      if (menuIdx < 0) return setOpenMenu(menuId);

      const newOpenMenu = menuHierarchy[openMenu][menuIdx - 1];
      return setOpenMenu(newOpenMenu ?? '');
    }
    const hierarchy = menuHierarchy[menuId];
    return setOpenMenu(hierarchy[hierarchy.length - 2]);
  };

  return (
    <ProSidebar
      className={[
        'relative !h-screen !w-52 !min-w-52 [&>ul]:flex [&>ul]:flex-col [&>ul]:gap-2 [&>ul]:!pb-0 [&>ul]:!pt-2',
        '[&_.pro-menu]:!py-0 [&_.pro-sidebar-layout]:!bg-bg-primary',
        '[&_.pro-menu>ul:first-of-type]:!pt-0 [&_.pro-menu]:!px-2',
        '[&_.pro-arrow]:!border-bg-secondary',
        '[&_.pro-icon-wrapper]:!mr-2 [&_.pro-icon-wrapper]:!block [&_.pro-icon-wrapper]:!size-auto',
        '[&_.pro-icon-wrapper]:!min-w-[auto]',
        '[&_.pro-inner-item]:rounded-md [&_.pro-inner-item]:!p-2 [&_.pro-inner-item]:transition',
        '[&_.pro-inner-item]:h-8 [&_.pro-inner-item]:ease-linear',
        '[&_.pro-inner-item:hover]:!bg-bg-secondary [&_.pro-inner-item]:before:!hidden',
        '[&_.pro-inner-item:hover_*]:text-bg-primary',
        '[&_.pro-inner-item:hover_.pro-arrow]:!border-bg-secondary',
        '[&_.pro-menu-item_*]:text-bg-secondary',
        '[&_.pro-menu-item.active_.pro-inner-item]:!bg-bg-secondary',
        '[&_.pro-menu-item.active_*]:text-bg-primary',
        '[&_.pro-inner-list-item]:!px-4 [&_.pro-inner-list-item]:!py-0 [&_.pro-inner-list-item]:transition',
        '[&_.pro-inner-list-item]:!list-none [&_.pro-inner-list-item]:!bg-transparent',
        '[&_ul]:flex [&_ul]:flex-col [&_ul]:gap-2 [&_ul]:!pb-0 [&_ul]:!pt-2',
      ].join(' ')}
    >
      <TruktWatermark className="absolute !-left-[10.5rem] bottom-[0.1rem] -z-[1]" width={450} />
      <div className="flex flex-col gap-1">
        <Link className="mb-2" to="/">
          <SidebarLogo logo={logo} />
        </Link>

        {accountInfo?.name && (
          <Typography className="!text-center !text-secondary-400">{accountInfo.name}</Typography>
        )}

        {accountInfo?.username && (
          <Typography className="!text-center !text-secondary-400">
            {accountInfo.username}
          </Typography>
        )}
      </div>
      <SidebarDivider />
      <div id="sidebar-scroll-container" className="h-[80vh] overflow-auto">
        <Menu>
          {items.map((item) => (
            <SidebarSubMenu
              key={item.id}
              item={item}
              menuHierarchy={menuHierarchy}
              onOpenChange={onOpenChange}
              openMenu={openMenu}
            />
          ))}
        </Menu>
      </div>
      {itemsSuffix && (
        <>
          {itemsSuffix}
          <SidebarDivider />
        </>
      )}
      <Typography className="!text-center !text-secondary-400">Version: {version}</Typography>
      <SidebarDivider />
      <Menu>
        <SidebarMenuItem
          icon={<BiArrowFromRight className="size-4 text-bg-secondary" />}
          label="Logout"
          onClick={onLogoutClick}
        />
      </Menu>
    </ProSidebar>
  );
};
